import { Component, OnInit, OnDestroy } from "@angular/core";
import { ActivatedRoute, Data, Router, UrlSegment } from '@angular/router';
import { Subject, merge, BehaviorSubject, combineLatest, of, forkJoin } from 'rxjs';
import { takeUntil, first, flatMap, filter, map, tap, delay } from 'rxjs/operators';
import { FormControl } from '@angular/forms';
import { ItemContextModel, StreamItemRequestModel } from '../model';
import { TenantService } from '../services/tenant.service';
import { StreamService } from '../services/stream.service';
import { ItemService } from "../services/item.service";
import { PermissionService } from '../services';
import * as _ from 'lodash';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmationDialogComponent } from '../general/confirmationDialog.component';

const MAX_PAGES = 3;
@Component({
    styles: [`
        .scroll-container {
            max-height: calc(100vh - 367px);
            height: calc(100vh - 367px);
            overflow-y: auto;
        }

        .scroll-container-large {
            max-height: calc(100vh - 178px);
            overflow-y: auto;
        }
    `],
    template: `
        <span *ngIf="!showItem">
            <mat-toolbar *ngIf="!isSource" style="height: 40px;">
                <button *ngIf="canEditTopic" style="min-width: 87px;" mat-button [routerLink]="['post']">+ Item</button>
                <label style="padding-left: 25px; padding-right: 10px; font-size: 14px;">Display:</label>
                <mat-select placeholder="Show" [formControl]="show" style="max-width: 85px; padding-right: 15px; font-size: 14px;">
                    <mat-option value="all">All</mat-option>
                    <mat-option value="displayed">Displayed</mat-option>
                    <mat-option value="hidden">Hidden</mat-option>
                    <mat-option value="training">Training</mat-option>
                </mat-select>
                <span fxFlex></span>
                <div style="font-size: 14px;">
                    <label id="visibility-label" style="padding-right: 10px;">Default Visibility</label>
                    <mat-radio-group aria-labelledby="visibility-label" [formControl]="curated">
                        <mat-radio-button [value]="false" style="padding-right: 10px;">Show</mat-radio-button>
                        <mat-radio-button [value]="true" style="padding-right: 10px;">Hide</mat-radio-button>
                    </mat-radio-group>
                </div>
                <label style="padding-left: 15px; padding-right: 10px; font-size: 14px;">Auto Curate:</label>
                <mat-select [formControl]="autoCurationRank" style="font-size: 14px; max-width: 45px;">
                    <mat-select-trigger>
                        <span *ngIf="!autoCurationRank.value">Off</span>
                        <curation-rank *ngIf="autoCurationRank.value" [score]="autoCurationRank.value" size="small"></curation-rank>
                    </mat-select-trigger>
                    <mat-option [value]="0">Off</mat-option>
                    <mat-option [value]="1"><curation-rank [score]="1" size="small"></curation-rank></mat-option>
                    <mat-option [value]="2"><curation-rank [score]="2" size="small"></curation-rank></mat-option>
                    <mat-option [value]="3"><curation-rank [score]="3" size="small"></curation-rank></mat-option>
                </mat-select>
                <help-link style="padding-left: 10px; padding-top: 5px;" articleSlug="114885-curation-profiles-and-auto-curation"></help-link>
            </mat-toolbar>
            <div *ngIf="noTouching" fxLayout="column" fxLayoutAlign="center center" style="height:100%">
                <spinner syle="padding-top: 100px;"></spinner>
            </div>
            <div [class]="isSource ? 'scroll-container-large' : 'scroll-container'" 
                infiniteScroll
                [infiniteScrollThrottle]="300"
                [infiniteScrollDisabled]="isLastPage"
                [scrollWindow]="false"
                (scrolled)="nextPage()">
                <span *ngFor="let item of item$ | async">
                    <item-summary [item]="item" 
                                [topic]="topic" 
                                [isAdmin]="true" 
                                baseRouteLink="./"
                                [curating]="!isSource"
                                (curationSetChanged)="curationSetChanged()"
                                [visibilityDisabled]="isSource"
                                (visibilityChanged)="visibilityChanged()"></item-summary>
                </span>
                <div *ngIf="noTouchingPaging" fxLayoutAlign="center center">
                    <spinner></spinner>
                </div>
            </div>
        </span>
        <router-outlet></router-outlet>
    `
})
export class EditTopicContentItemsTabComponent implements OnInit, OnDestroy {
    private cleanupTime = new Subject<void>();
    tenant: any;
    noTouching: boolean;
    itemSearchParams = new StreamItemRequestModel(-1);
    isLastPage: any;
    totalElements: any;
    item$ = new BehaviorSubject<any[]>([]);
    noTouchingPaging: boolean;
    show = new FormControl('all');
    autoCurationRank = new FormControl();
    curated = new FormControl();
    topic: any = {};
    contextTopicSubject = new BehaviorSubject(this.topic);
    expandedElement: any;
    isSource: boolean;
    detailHeaders = [];
    detailColumns = ['title', 'visibility', 'created'];
    canEditTopic: boolean;
    showItem: boolean;
    
    constructor(private router: Router,
                private route: ActivatedRoute,
                private dialog: MatDialog, 
                private itemService: ItemService,
                private permissionService: PermissionService, 
                private streamService: StreamService,
                private tenantService: TenantService) {}
    
    ngOnInit(): void {
        this.showItem = !!this.route.firstChild;
        this.route.data.pipe(takeUntil(this.cleanupTime)).subscribe((data: Data) => {
            this.isSource = data.isSource;
            if (this.isSource) {
                this.detailHeaders = ['title', 'published', 'created'];
                this.detailColumns = ['title', 'published', 'created'];
            }
        });

        this.tenantService.getCurrentTenant().pipe(first())
        .subscribe(tenant => {
            this.tenant = tenant;
        });

        this.itemSearchParams.page = 0;
        merge(this.route.parent.params, 
              this.route.parent.parent.params, 
              this.route.parent.parent.parent.params,
              this.route.parent.parent.parent.parent.params) 
        .pipe(
            filter(params => !!params.topicId),
            flatMap(params => this.streamService.getStream(params.topicId)),
            takeUntil(this.cleanupTime)
        )
        .subscribe(topic => {
            this.topic = topic;
            this.curated.setValue(topic.curated, { emitEvent: false });
            this.autoCurationRank.setValue(topic.autoCurationRank || 0, { emitEvent: false });
            this.contextTopicSubject.next(topic);
            this.canEditTopic = this.topic &&
                            this.topic.tenantId &&
                            (this.topic.tenantId === this.tenant.id) &&
                            this.topic.type === 'COLLECTION' &&
                            this.permissionService.currentUserCanManageStream(this.topic);
        });

        this.itemService.itemClosing.pipe(takeUntil(this.cleanupTime))
        .subscribe(() => {
            const pathFromRoot = this.route.snapshot.pathFromRoot;
            this.router.navigateByUrl(pathFromRoot.slice(0, pathFromRoot.length - 1).map(snap => snap.url[0]?.path || '').join('/'));
        });
        this.itemService.itemContextChanged.pipe(
            delay(2000),
            takeUntil(this.cleanupTime)
        ).subscribe(() => {
            this.itemSearchParams.page = 0;
            this.noTouching = true;
            this.item$.next([]);
            this.fetchItems();
        });

        this.route.url.pipe(takeUntil(this.cleanupTime))
        .subscribe((segs: UrlSegment[]) => {
            if (segs.length) {
                if (segs[0].path === 'post') {
                    this.itemService.focusedItem.next(undefined);
                    this.itemService.focusedItemEdit.next({ topicId: this.topic.id });
                } else {
                    const itemId = segs[0].path;
                    let item = this.item$.value.find(item => item.id === itemId);
                    if (item) {
                        this.itemService.focusedItem.next(new ItemContextModel(item, undefined, undefined, this.topic, this.isSource, !this.isSource));                        
                    } else {
                        forkJoin([
                            this.streamService.getStream(this.route.snapshot.params.topicId),
                            this.itemService.getItem(itemId)
                        ])
                        .subscribe(([topic, { item }]) => this.itemService.focusedItem.next(new ItemContextModel(item, undefined, undefined, topic, this.isSource, !this.isSource)));
                    }
                }
            } else {
                this.itemService.focusedItem.next(undefined);
                this.itemService.focusedItemEdit.next(undefined);
            }
        });

        this.show.valueChanges
        .pipe(takeUntil(this.cleanupTime))
        .subscribe(mode => {
            this.item$.next([]);
            this.itemSearchParams.displayedOnly =  mode === 'displayed';
            this.itemSearchParams.hiddenOnly = mode === 'hidden';
            this.noTouching = true;
            this.itemSearchParams.page = 0;
            this.fetchItems();
        });

        this.curated.valueChanges
        .pipe(
            flatMap(curated => {
                const message = curated ?
                '<p>Only Administrators and the Topic Owner will see items until they are unhidden.</p> <p>Click OK to confirm.</p>' :
                '<p>Items will be visible by default to all users. Administrators and Topic owners will be able to hide selected items.</p> <p>Click OK to confirm.</p>';
                return this.dialog.open(ConfirmationDialogComponent, { 
                    data: {
                        title: 'Set Default Visibility',
                        content: message, 
                        actionLabel: 'OK'}
                    }).afterClosed()
            }),
            filter(result => {
                if (!result) {
                    this.curated.setValue(this.topic.curated, { emitEvent: false });
                }
                return result
            }),
            tap(() => this.noTouchingPaging = true),
            tap(() => this.item$.next([])),
            flatMap(() => {
                this.topic.curated = !this.topic.curated;
                this.topic.autoCurationRank = 0;
                this.autoCurationRank.setValue(this.topic.autoCurationRank, { emitEvent: false });
                return this.streamService.updateStream(this.topic);
            }),
            tap(() => this.streamService.streamUpdates.next(this.topic)),
            tap(() => this.itemSearchParams.page = 0),
            tap(() => this.fetchItems()),
            takeUntil(this.cleanupTime)
        ).subscribe(() => this.curated.setValue(this.topic.curated, { emitEvent: false }));

        this.autoCurationRank.valueChanges
        .pipe(
            flatMap((value) => {
                const message = this.topic.curated ?
                `This change will cause all new items with a curation rank equal to ${value} or above to be automatically shown.` :
                `This change will cause all new items with a curation rank below ${value} to be automatically hidden.`;
                return forkJoin([
                    value !== 0 ? this.dialog.open(ConfirmationDialogComponent, { data: {
                        title: 'Set Auto Curation Visibility',
                        content: message, 
                        actionLabel: 'OK'}
                    }).afterClosed() : of(true), 
                    of(value)
                ]); 
            }),
            filter(([doIt]) => {
                if (!doIt) {
                    this.autoCurationRank.setValue(this.topic.autoCurationRank, { emitEvent: false });
                }
                return doIt
            }),
            map(([, value])=> value),
            flatMap((value) => {
                this.topic.autoCurationRank = value;
                return this.streamService.updateStream(this.topic);
            }),
            tap(() => this.streamService.streamUpdates.next(this.topic)),
            tap(() => this.itemSearchParams.page = 0),
            tap(() => this.fetchItems()),
            takeUntil(this.cleanupTime)
        ).subscribe();

        this.noTouching = true;
        this.itemSearchParams.page = 0;
        this.itemSearchParams.displayedOnly = false;
        this.itemSearchParams.hiddenOnly = false;
        this.fetchItems();
    }

    nextPage() {
        if (!this.isLastPage) {
            this.itemSearchParams.page++;
            this.noTouchingPaging = true;
            this.fetchItems();
        }
    }

    get topicId() {
        // this might seem like extra since we are going to fetch the topic as well, 
        // but this ensures that we are operating with the latest param at all times
        return this.route.snapshot.parent.params.topicId || 
               this.route.snapshot.parent.parent.params.topicId || 
               this.route.snapshot.parent.parent.parent.params.topicId ||
               this.route.snapshot.parent.parent.parent.parent.params.topicId;
    }

    fetchItems() {
        if (this.show.value === 'training') {
            this.streamService.getStreamCurationItems(this.topicId, this.itemSearchParams)
            .subscribe(results => {
                this.item$.next(results)
                this.totalElements = results.length;
                this.isLastPage = true;
                this.noTouching = false;
                this.noTouchingPaging = false;
            });
        } else {
            this.streamService.getStreamItems(this.topicId, this.itemSearchParams)
            .subscribe(results => {
                this.item$.next((results.first ? [] : this.item$.value).concat(results.content))
                this.totalElements = results.totalElements;
                this.isLastPage = results.last;
                this.noTouching = false;
                this.noTouchingPaging = false;
            });
        }
    }

    toggleVisibility(item, event: MouseEvent) {
        event.stopImmediatePropagation();
        item.visible = !item.visible;
        const patch = this.topic.curated ? { exposed: item.visible } : { hidden: !item.visible };
        this.itemService.patchItem(this.topicId, item.id, patch).pipe(first()).subscribe();
    }

    visibilityChanged(): void {
        this.streamService.streamUpdates.next(this.topic);
    }

    curationSetChanged(): void {
        this.streamService.getStream(this.topic.id, true).subscribe(topic => this.topic = topic);
        if (this.itemSearchParams.page < MAX_PAGES) {
            const obs = _.range(0, Math.min(this.itemSearchParams.page + 1, MAX_PAGES)).map(idx => this.streamService.getStreamItems(this.topicId, 
                Object.assign(_.clone(this.itemSearchParams), { page: idx }))
            );
            combineLatest(obs)
            .pipe(
                map((results: any[]) => results.reduce((acc, res) => {
                        res.content.forEach(item => acc[item.id] = item);
                        return acc;
                    }, {}))
            )
            .subscribe(itemMap => {
                this.item$.value.forEach(item => {
                    if (itemMap[item.id]) {
                        item.curationRank = itemMap[item.id].curationRank; 
                    }
                });
            });
        } else {
            this.noTouchingPaging = true;
            this.item$.next([]);
            this.itemSearchParams.page = 0;
            this.fetchItems();
        }
    }

    ngOnDestroy(): void {
        this.itemService.focusedItem.next(undefined);
        this.cleanupTime.next();
        this.cleanupTime.complete();
    }
}
